import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil, finalize, take, filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { State } from '../../../../store';
import { areasSelector } from '../../../store/global.selectors';
import { GetOrganisationAreasAction } from '../../../store/global.actions';
import { GetAdminOrganisationAreas } from '../../../global.model';
import {
    CollectsAnalyticsPeriod,
    CollectsAnalyticsQuery,
    CollectsSnapshotResponse,
    CollectsDistributionResponse,
    CollectsTrendsResponse,
    CollectsGeoResponse,
} from '../../../services/collects-analytics/collects-analytics.model';
import { CollectsAnalyticsService } from '../../../services/collects-analytics/collects-analytics.abstract.service';

@Component({
    selector: 'app-collections-analytics',
    templateUrl: './collections-analytics.component.html',
    styleUrls: ['./collections-analytics.component.scss'],
})
export class CollectionsAnalyticsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    subTabs = [
        { id: 'engagement', labelKey: 'pages.collections.analytics.tab_engagement' },
        { id: 'reminders', labelKey: 'pages.collections.analytics.tab_reminders' },
    ];
    selectedTab = 'engagement';

    selectedPeriod: CollectsAnalyticsPeriod = '30d';
    comparePrevious = false;
    customFrom: string | null = null;
    customTo: string | null = null;
    showCustomDates = false;

    selectedAreaId: string | null = null;
    areas: GetAdminOrganisationAreas[] = [];

    loading = false;
    error = false;

    snapshot: CollectsSnapshotResponse | null = null;
    distribution: CollectsDistributionResponse | null = null;
    trends: CollectsTrendsResponse | null = null;
    geo: CollectsGeoResponse | null = null;

    periods: { value: CollectsAnalyticsPeriod; labelKey: string }[] = [
        { value: '7d', labelKey: 'pages.collections.analytics.period_7d' },
        { value: '30d', labelKey: 'pages.collections.analytics.period_30d' },
        { value: '90d', labelKey: 'pages.collections.analytics.period_90d' },
        { value: 'custom', labelKey: 'pages.collections.analytics.period_custom' },
    ];

    constructor(
        private analyticsService: CollectsAnalyticsService,
        public translate: TranslateService,
        private store: Store<State>,
    ) {}

    ngOnInit(): void {
        this.loadAreas();
        this.loadData();
        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.loadAreas();
            this.loadData();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadAreas(): void {
        this.store
            .select(areasSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((areas) => {
                this.areas = areas || [];
            });

        this.store
            .select(areasSelector)
            .pipe(
                take(1),
                filter((areas) => areas.length === 0),
            )
            .subscribe(() => this.store.dispatch(GetOrganisationAreasAction()));
    }

    onSubTabChange(tab: string): void {
        this.selectedTab = tab;
    }

    onPeriodChange(period: CollectsAnalyticsPeriod): void {
        this.selectedPeriod = period;
        this.showCustomDates = period === 'custom';
        if (period !== 'custom') {
            this.customFrom = null;
            this.customTo = null;
            this.loadData();
        }
    }

    onCompareToggle(): void {
        this.comparePrevious = !this.comparePrevious;
        this.loadData();
    }

    onAreaChange(areaId: string): void {
        this.selectedAreaId = areaId || null;
        this.loadData();
    }

    onCustomDateChange(): void {
        if (this.customFrom && this.customTo) {
            this.loadData();
        }
    }

    retry(): void {
        this.loadData();
    }

    loadData(): void {
        if (this.selectedPeriod === 'custom' && (!this.customFrom || !this.customTo)) {
            return;
        }

        const query: CollectsAnalyticsQuery = {
            period: this.selectedPeriod,
            comparePrevious: this.comparePrevious,
        };

        if (this.selectedPeriod === 'custom') {
            query.from = this.customFrom;
            query.to = this.customTo;
        }

        if (this.selectedAreaId) {
            query.areaId = this.selectedAreaId;
        }

        this.loading = true;
        this.error = false;

        forkJoin({
            snapshot: this.analyticsService.getSnapshot(query),
            distribution: this.analyticsService.getDistribution(query),
            trends: this.analyticsService.getTrends(query),
            geo: this.analyticsService.getGeo(query),
        })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe({
                next: (result) => {
                    this.snapshot = result.snapshot;
                    this.distribution = result.distribution;
                    this.trends = result.trends;
                    this.geo = result.geo;
                },
                error: () => {
                    this.error = true;
                    this.snapshot = null;
                    this.distribution = null;
                    this.trends = null;
                    this.geo = null;
                },
            });
    }
}
